import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import { STATUS } from 'dpl/breeder_preferred_qualifications/utils/constants';
import Icon from 'dpl/common/components/Icon';
import { BUTTON_VARIANTS } from 'dpl/common/design_system/Button';
import BreederProfileDetailsCardSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileDetailsCardSkeleton';
import {
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import {
  breederTransportOptionsQueryDefinition,
  singleBreederProfileResourceDefinition
} from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { FILTER_LOCATION_NAME } from 'dpl/constants/query_params';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import { getLocationDescription } from 'dpl/components/BreederProfile/mobile/components/overview/OverviewCard';

import BreederProfileApplyButtonContainer from '../BreederProfileApplyButtonContainer';
import { SECTION_ID as STORIES_SECTION_ID } from '../BreederProfileOwnerStoriesContainer';
import BreederProfileFollowButtonContainer from '../BreederProfileFollowButtonContainer';

const IS_LT_MD = isLessThanBreakpoint('md');

const COMMON_ANALYTICS_PROPS = {
  view: ANALYTICS_VIEWS.BREEDER_PROFILE,
  view_feature_location:
    ANALYTICS_VIEW_FEATURE_LOCATIONS.BREEDER_PROFILE_OVERVIEW
};

function scrollToSection(sectionId) {
  const section = document.getElementById(sectionId);
  if (section) section.scrollIntoView({ behavior: 'smooth' });
}

function DetailsCardItem({ iconName, description }) {
  return (
    <li className="flex gap-8">
      <Icon
        className="BreederProfileDetailsCard__icon"
        name={iconName}
        height="20px"
        width="20px"
      />
      {description}
    </li>
  );
}

DetailsCardItem.propTypes = {
  iconName: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired
};

export default function BreederProfileDetailsCardContainer({ className }) {
  const {
    server: { breederProfileId, testimonialsCount, breederPreferredStatus },
    queryParams: { [FILTER_LOCATION_NAME]: locName }
  } = useSelector(({ server, queryParams }) => ({ server, queryParams }));

  const { value: breeder, isFetching: isFetchingBreeder } = useResourceQuery(
    singleBreederProfileResourceDefinition(breederProfileId)
  );

  const isPreferredBreeder = breederPreferredStatus === STATUS.PREFERRED;

  const {
    value: {
      data: { location }
    },
    isFetching: isFetchingTransportOptions
  } = useResourceQuery(
    breederTransportOptionsQueryDefinition(breederProfileId, { locName })
  );

  const hasListItems =
    breeder.meet_up_availability_sentence ||
    breeder.response_time_description ||
    testimonialsCount > 0;

  if (isFetchingBreeder) {
    return <BreederProfileDetailsCardSkeleton />;
  }

  return (
    <div
      className={classnames(
        'BreederProfileDetailsCardContainer flex flex-column justify-between br4 ba b--default overflow-hidden',
        className
      )}
      style={{ minHeight: !IS_LT_MD ? 500 : null }}
    >
      <div className="tc-ns bg-sand pv4 ph4">
        <div className="dib relative mb4 mb2-md">
          <UserAvatar
            size="xxl"
            user={{
              id: breeder.user.id,
              initials: breeder.initials,
              profile_photo_url: breeder.has_profile_photo
                ? breeder.profile_photo_url
                : null
            }}
          />
          {isPreferredBreeder && (
            <PreferredBreederBadge
              className="absolute right-0 bottom-0"
              width="20px"
              height="22px"
              hideTooltip
            />
          )}
        </div>
        <p className="font-serif font-24 md:font-32 lh-title mb1">
          {breeder.display_name}
        </p>
        <p className="font-14 md:font-18 secondary">
          Breeder of{' '}
          <span
            className="stone-700"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: breeder.linked_breed_names }}
          />
        </p>
      </div>

      <div
        className={classnames({
          dn: !hasListItems && IS_LT_MD,
          'flex flex-column justify-between flex-auto pv4 ph4 pv6-md ph6-md':
            hasListItems || !IS_LT_MD
        })}
      >
        {hasListItems && (
          <ul className="flex flex-column gap-4 md:gap-8 font-14 md:font-18 secondary">
            {isFetchingTransportOptions ? (
              <li>
                <SkeletonBlock
                  className="font-14 md:font-18"
                  height="1.4em"
                  width="100%"
                />
              </li>
            ) : (
              <DetailsCardItem
                iconName="fetch-location"
                description={
                  <span>{getLocationDescription({ location })}</span>
                }
              />
            )}
            {breeder.meet_up_availability_sentence && (
              <DetailsCardItem
                iconName="fetch-transportation-car"
                description={
                  <span>{breeder.meet_up_availability_sentence}</span>
                }
              />
            )}
            {breeder.response_time_description && (
              <DetailsCardItem
                iconName="fetch-message"
                description={<span>{breeder.response_time_description}</span>}
              />
            )}
            {testimonialsCount > 0 && (
              <DetailsCardItem
                iconName="fetch-review"
                description={
                  <button
                    type="button"
                    className="underline"
                    onClick={() => scrollToSection(STORIES_SECTION_ID)}
                  >
                    {testimonialsCount}{' '}
                    {testimonialsCount === 1 ? 'review' : 'reviews'}
                  </button>
                }
              />
            )}
          </ul>
        )}
        {!IS_LT_MD && (
          <div className="flex flex-column gap-8 mt-auto">
            <BreederProfileApplyButtonContainer
              className={classnames(BUTTON_VARIANTS.primary, 'font-16 pv3 mt6')}
              analytics={{
                ...COMMON_ANALYTICS_PROPS,
                view_feature: ANALYTICS_VIEW_FEATURES.APPLY_TO_BREEDER_BUTTON
              }}
            />
            <BreederProfileFollowButtonContainer
              className={classnames(
                BUTTON_VARIANTS.outline,
                'flex items-center justify-center font-16 pv3'
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

BreederProfileDetailsCardContainer.propTypes = {
  className: PropTypes.string
};

BreederProfileDetailsCardContainer.defaultProps = {
  className: null
};
